import { jsx as _jsx } from "react/jsx-runtime";
import { PregnancyProvider, Spinner } from "@madmedical/ui";
import { rangeToString } from "@madmedical/utils";
import { MetricType, UnderlyingCondition } from "@madmedical/medical";
import { usePatient } from "@madmedical/user";
import { useEffect, useState } from "react";
import { useGetMeasurementRangeQuery } from "../../measurement/api";
import { useGetPregnancyByPatientQuery, useGetPregnancyWellBeingsQuery, } from "../api";
var ModalState;
(function (ModalState) {
    ModalState[ModalState["Closed"] = 0] = "Closed";
    ModalState[ModalState["Measure"] = 1] = "Measure";
    ModalState[ModalState["Feeling"] = 2] = "Feeling";
    ModalState[ModalState["Edit"] = 3] = "Edit";
    ModalState[ModalState["Delete"] = 4] = "Delete";
})(ModalState || (ModalState = {}));
const PregnancyProviderWrapper = ({ children, setModal }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    const { patientId, patient, isLoading: isPatientLoading } = usePatient();
    const [pregnancyWellBeingData, setPregnancyWellBeingData] = useState(undefined);
    const [deletePregncancyWellBeingId, setDeletePregncancyWellBeingId] = useState();
    // console.log("patient", patient);
    const typedPatient = patient;
    const isPregnant = (_a = typedPatient === null || typedPatient === void 0 ? void 0 : typedPatient.underlyingConditions) === null || _a === void 0 ? void 0 : _a.includes(UnderlyingCondition.Pregnancy);
    const { data: pregnancyData, isLoading: isPregnancyLoading } = useGetPregnancyByPatientQuery({
        patientId,
    }, {
        skip: !isPregnant,
    });
    const pregnancyStartDate = (pregnancyData === null || pregnancyData === void 0 ? void 0 : pregnancyData.startedAt) && new Date(pregnancyData === null || pregnancyData === void 0 ? void 0 : pregnancyData.startedAt);
    const pregnancyEndDate = (pregnancyData === null || pregnancyData === void 0 ? void 0 : pregnancyData.expectedAt) && new Date(pregnancyData === null || pregnancyData === void 0 ? void 0 : pregnancyData.expectedAt);
    const prePregnancyStart = (pregnancyData === null || pregnancyData === void 0 ? void 0 : pregnancyData.startedAt) &&
        pregnancyStartDate &&
        new Date(pregnancyStartDate.getTime() - 60 * 24 * 3600 * 1000); // 60 days before pregnancy start
    const dateRange = {
        from: (pregnancyStartDate !== null && pregnancyStartDate !== void 0 ? pregnancyStartDate : new Date()),
        to: (pregnancyEndDate !== null && pregnancyEndDate !== void 0 ? pregnancyEndDate : new Date()),
    };
    const prePregnancyRange = {
        from: (prePregnancyStart !== null && prePregnancyStart !== void 0 ? prePregnancyStart : new Date()),
        to: (pregnancyStartDate !== null && pregnancyStartDate !== void 0 ? pregnancyStartDate : new Date()),
    };
    const { data: weightData, isLoading: isWeightLoading } = useGetMeasurementRangeQuery({
        userId: patientId,
        metricType: MetricType.BodyWeight,
        queryRange: rangeToString(dateRange),
    });
    const { data: bmiData, isLoading: isBmiLoading, refetch: refetchBMI, } = useGetMeasurementRangeQuery({
        userId: patientId,
        metricType: MetricType.BodyMassIndex,
        queryRange: rangeToString(dateRange),
    });
    // previous pregnancy data
    const { data: previousWeightData } = useGetMeasurementRangeQuery({
        userId: patientId,
        metricType: MetricType.BodyMassIndex,
        queryRange: rangeToString(prePregnancyRange),
    }, {
        skip: !isPregnant,
    });
    const { data: previousBmiData } = useGetMeasurementRangeQuery({
        userId: patientId,
        metricType: MetricType.BodyMassIndex,
        queryRange: rangeToString(prePregnancyRange),
    }, {
        skip: !isPregnant,
    });
    // well beings
    const [wellBeingFilters, setWellBeingFilters] = useState();
    const adjustWellBeingFilters = (filters) => {
        setWellBeingFilters((prevState) => (Object.assign(Object.assign({}, prevState), filters)));
    };
    const { data: wellBeingData, isLoading: isWellbeingLoading, isError: isWellbeingError, refetch: refetchWellBeings, } = useGetPregnancyWellBeingsQuery({
        patientId,
        status: wellBeingFilters === null || wellBeingFilters === void 0 ? void 0 : wellBeingFilters.status,
        symptoms: wellBeingFilters === null || wellBeingFilters === void 0 ? void 0 : wellBeingFilters.symptoms,
    });
    useEffect(() => {
        if (wellBeingFilters && !isWellbeingError) {
            void refetchWellBeings();
        }
    }, [wellBeingFilters, refetchWellBeings, isWellbeingError]);
    const onEditPress = (mood) => {
        setPregnancyWellBeingData(mood);
        setModal(ModalState.Edit);
    };
    const onDeletePress = (id) => {
        setDeletePregncancyWellBeingId(id);
        setModal(ModalState.Delete);
    };
    const { data: circumferenceData, isLoading: isCircumferenceLoading } = useGetMeasurementRangeQuery({
        userId: patientId,
        metricType: MetricType.AbdominalCircumference,
        queryRange: rangeToString(dateRange),
    });
    const refetchBmiWrapper = () => {
        void refetchBMI();
    };
    const previousBmi = (previousBmiData === null || previousBmiData === void 0 ? void 0 : previousBmiData.items.length)
        ? previousBmiData.items[previousBmiData.items.length - 1]
        : undefined;
    const firstMeasuredBmi = previousBmi
        ? +(previousBmi === null || previousBmi === void 0 ? void 0 : previousBmi.metric.displayValue)
        : ((_c = (_b = bmiData === null || bmiData === void 0 ? void 0 : bmiData.items) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.metric.displayValue)
            ? +((_e = (_d = bmiData === null || bmiData === void 0 ? void 0 : bmiData.items) === null || _d === void 0 ? void 0 : _d[0]) === null || _e === void 0 ? void 0 : _e.metric.displayValue)
            : 0;
    const previousWeight = (previousWeightData === null || previousWeightData === void 0 ? void 0 : previousWeightData.items.length)
        ? previousWeightData.items[previousWeightData.items.length - 1]
        : undefined;
    const firstMeasuredWeight = previousWeight
        ? +(previousWeight === null || previousWeight === void 0 ? void 0 : previousWeight.metric.displayValue)
        : ((_g = (_f = weightData === null || weightData === void 0 ? void 0 : weightData.items) === null || _f === void 0 ? void 0 : _f[0]) === null || _g === void 0 ? void 0 : _g.metric.displayValue)
            ? +((_j = (_h = weightData === null || weightData === void 0 ? void 0 : weightData.items) === null || _h === void 0 ? void 0 : _h[0]) === null || _j === void 0 ? void 0 : _j.metric.displayValue)
            : 0;
    if (isWeightLoading ||
        isBmiLoading ||
        isPatientLoading ||
        isPregnancyLoading ||
        isWellbeingLoading ||
        isCircumferenceLoading) {
        return _jsx(Spinner, {});
    }
    return (_jsx(PregnancyProvider, Object.assign({ value: {
            selectedRange: dateRange,
            weightData,
            bmiData,
            circumferenceData,
            pregnancyData,
            pregnancyWellBeing: (_k = wellBeingData === null || wellBeingData === void 0 ? void 0 : wellBeingData.items) !== null && _k !== void 0 ? _k : [],
            onEditPress,
            onDeletePress,
            pregnancyWellBeingData,
            deletePregncancyWellBeingId,
            setModal,
            adjustWellBeingFilters,
            patient: typedPatient,
            refetchBMI: refetchBmiWrapper,
            firstMeasuredBmi,
            firstMeasuredWeight,
        } }, { children: children })));
};
export default PregnancyProviderWrapper;
